'use client';

import React, { useCallback, useEffect, useState } from 'react';
import {
  PROGRAM_TYPE_OPTIONS,
  EducationProgramTypeValue,
} from './constants';

interface EducationProgramTypeComponentProps {
  componentId: string;
  initialEducationProgramType?: Array<
    { value: EducationProgramTypeValue; label: string } | string
  >;
  onUpdate: (componentId: string, data: any) => void;
  onRemove: (componentId: string) => void;
  fromPlaceholder?: boolean;
}

const EducationProgramTypeComponent: React.FC<
  EducationProgramTypeComponentProps
> = ({
  componentId,
  initialEducationProgramType,
  onUpdate,
  onRemove,
  fromPlaceholder = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Track if this is a new component from a placeholder that hasn't been confirmed yet
  const [isNewFromPlaceholder, setIsNewFromPlaceholder] = useState(
    fromPlaceholder &&
      (!initialEducationProgramType || initialEducationProgramType.length === 0)
  );

  // Get initial value from props or default to null (no selection yet)
  const getInitialValue = (): EducationProgramTypeValue | null => {
    if (initialEducationProgramType && initialEducationProgramType.length > 0) {
      const value = initialEducationProgramType[0];
      // Handle case where value might be a string (from hydration)
      if (typeof value === 'string') {
        return value as EducationProgramTypeValue;
      }
      return value?.value ?? null;
    }
    return null;
  };

  const [selectedValue, setSelectedValue] =
    useState<EducationProgramTypeValue | null>(getInitialValue());

  // Update Redux when selection changes
  const updateRedux = useCallback(
    (programTypeValue: EducationProgramTypeValue) => {
      onUpdate(componentId, {
        type: 'education_program_type',
        value: programTypeValue,
      });
    },
    [componentId, onUpdate]
  );

  // Auto-open dropdown if starting with no selection (new component from placeholder)
  useEffect(() => {
    if (!selectedValue) {
      setIsOpen(true);
    } else {
      // Register existing value on mount
      updateRedux(selectedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const handleSelect = useCallback(
    (value: EducationProgramTypeValue) => {
      setSelectedValue(value);
      setIsOpen(false);
      setIsNewFromPlaceholder(false);
      updateRedux(value);
    },
    [updateRedux]
  );

  // Handle close without selection - remove if from placeholder
  const _handleClose = useCallback(() => {
    setIsOpen(false);
    if (isNewFromPlaceholder && !selectedValue) {
      onRemove(componentId);
    }
  }, [isNewFromPlaceholder, selectedValue, onRemove, componentId]);

  const handleRemove = useCallback(() => {
    onRemove(componentId);
  }, [componentId, onRemove]);

  const selectedOption = selectedValue
    ? PROGRAM_TYPE_OPTIONS.find((opt) => opt.value === selectedValue)
    : null;

  return (
    <span
      className="inline-flex items-center gap-1 text-cyan-600 text-sm font-semibold group relative"
      data-testid="education-program-type-component"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="inline-flex items-center gap-1 hover:text-cyan-700 cursor-pointer"
        data-testid="education-program-type-selector"
        aria-label="Select education program type"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span data-testid="education-program-type-label">
          {selectedOption?.label || 'Select program type...'}
        </span>
        <svg
          className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg z-50 min-w-36"
          role="listbox"
          aria-label="Education program type options"
        >
          {PROGRAM_TYPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={selectedValue === option.value}
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(option.value);
              }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-cyan-50 first:rounded-t-lg last:rounded-b-lg ${
                selectedValue === option.value
                  ? 'bg-cyan-100 text-cyan-700 font-medium'
                  : 'text-neutral-700'
              }`}
              data-testid={`education-program-type-option-${option.value}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {/* Remove button */}
      <button
        className={`text-xs transition-all ${
          isHovered
            ? 'text-red-500 hover:text-red-700 opacity-100'
            : 'text-gray-400 opacity-0 w-0 group-hover:opacity-100 group-hover:w-auto'
        }`}
        onClick={(e) => {
          e.stopPropagation();
          handleRemove();
        }}
        aria-label="Remove education program type"
        data-testid="education-program-type-remove-button"
      >
        x
      </button>
    </span>
  );
};

export default EducationProgramTypeComponent;
